import { Component } from '@theme/component';

const layers = new Set(['profile', 'music', 'video', 'photos', 'shop', 'contact', 'game']);

class ArtistExperience extends Component {
  connectedCallback() {
    super.connectedCallback();
    if (this.controller) return;
    this.controller = new AbortController();
    this.dialog = this.querySelector('[data-artist-dialog]');
    this.lightbox = this.querySelector('[data-artist-lightbox]');
    this.archive = this.querySelector('[data-artist-archive]');
    this.body = this.querySelector('[data-artist-dialog-body]');
    if (!this.dialog?.showModal || !this.archive || !this.body) return;
    this.owner = `${this.dataset.artist}-${Math.random().toString(36).slice(2)}`;
    this.syncing = false;
    this.panels = new Map();
    for (const panel of this.querySelectorAll('[data-artist-panel]')) {
      const placeholder = document.createComment(panel.dataset.artistPanel);
      panel.before(placeholder);
      this.panels.set(panel.dataset.artistPanel, { panel, placeholder });
    }
    this.archive.hidden = true;
    this.dataset.enhanced = 'true';
    const options = { signal: this.controller.signal };
    this.addEventListener('click', event => this.onClick(event), options);
    window.addEventListener('popstate', () => this.syncLocation(), options);
    window.addEventListener('hashchange', () => this.syncLocation(), options);
    this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.requestClose(); }, options);
    this.lightbox.addEventListener('cancel', event => { event.preventDefault(); this.requestClose(); }, options);
    this.dialog.addEventListener('close', () => { if (!this.syncing) this.closeLayer(); }, options);
    this.lightbox.addEventListener('close', () => this.restorePhotoFocus(), options);
    const fullscreen = this.querySelector('[data-artist-fullscreen]');
    if (fullscreen && document.fullscreenEnabled) {
      fullscreen.hidden = false;
      document.addEventListener('fullscreenchange', () => fullscreen.setAttribute('aria-pressed', String(document.fullscreenElement === this)), options);
    }
    this.syncLocation();
  }

  updatedCallback() {
    super.updatedCallback();
    this.disconnectedCallback();
    this.connectedCallback();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.syncing = true;
    this.lightbox?.close();
    this.dialog?.close();
    this.restorePanel();
    for (const record of this.panels?.values() || []) record.placeholder.remove();
    if (this.archive) this.archive.hidden = false;
    delete this.dataset.enhanced;
    super.disconnectedCallback();
  }

  onClick(event) {
    if (!(event.target instanceof Element)) return;
    const opener = event.target.closest('[data-artist-open]');
    if (opener && this.contains(opener) && layers.has(opener.dataset.artistOpen)) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
      event.preventDefault();
      if (!this.dialog.open) this.opener = opener;
      this.navigate(opener.dataset.artistOpen);
      return;
    }
    if (event.target.closest('[data-artist-close], [data-artist-photo-close]')) { this.requestClose(); return; }
    const photo = event.target.closest('.anr-ap__galitem');
    if (photo && this.contains(photo)) {
      this.photoOpener = photo;
      const photos = [...this.panels.get('photos').panel.querySelectorAll('.anr-ap__galitem')];
      this.navigate('photos', photos.indexOf(photo) + 1);
      return;
    }
    if (event.target.closest('[data-artist-fullscreen]')) {
      const action = document.fullscreenElement === this ? document.exitFullscreen() : this.requestFullscreen();
      action?.catch(() => {});
    }
    if (event.target === this.dialog || event.target === this.lightbox) {
      const rect = event.target.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.requestClose();
    }
  }

  navigate(layer, photo = 0) {
    const hash = `#artist-${layer}${photo ? `/photo-${photo}` : ''}`;
    if (location.hash !== hash) {
      if (this.dialog.open && !photo) history.replaceState(history.state, '', hash);
      else history.pushState({ ...history.state, anrArtistOwner: this.owner }, '', hash);
    }
    this.syncLocation();
  }

  syncLocation() {
    const match = location.hash.match(/^#artist-(profile|music|video|photos|shop|contact|game)(?:\/photo-(\d+))?$/);
    this.syncing = true;
    if (!match) {
      this.lightbox.close();
      this.closeLayer();
      this.syncing = false;
      return;
    }
    this.openLayer(match[1]);
    const index = Number(match[2] || 0);
    if (match[1] === 'photos' && index > 0) this.openPhoto(index);
    else this.lightbox.close();
    this.syncing = false;
  }

  requestClose() {
    if (history.state?.anrArtistOwner === this.owner) { history.back(); return; }
    const hash = this.lightbox.open ? '#artist-photos' : '#artist-world';
    history.replaceState(history.state, '', hash);
    this.syncLocation();
  }

  openLayer(layer) {
    const record = this.panels.get(layer);
    if (!record) return;
    const changed = this.layer !== layer;
    if (changed) {
      this.restorePanel();
      this.body.append(record.panel);
      this.layer = layer;
      this.dialog.dataset.layer = layer;
      this.body.scrollTop = 0;
    }
    for (const link of this.querySelectorAll('[data-artist-open]')) {
      if (link.dataset.artistOpen === layer) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    }
    if (!this.dialog.open) this.dialog.showModal();
    if (changed) record.panel.querySelector('.anr-experience__panel-title')?.focus({ preventScroll: true });
  }

  closeLayer() {
    if (!this.layer && !this.dialog.open) return;
    const opener = this.opener || this.querySelector(`.anr-experience__menu [data-artist-open="${this.layer}"]`);
    this.restorePanel();
    this.dialog.close();
    for (const link of this.querySelectorAll('[aria-current]')) link.removeAttribute('aria-current');
    opener?.focus({ preventScroll: true });
    this.opener = null;
  }

  restorePanel() {
    const record = this.panels?.get(this.layer);
    if (!record) return;
    for (const frame of record.panel.querySelectorAll('iframe')) {
      if (frame.src) frame.src = frame.src;
    }
    for (const video of record.panel.querySelectorAll('video, audio')) video.pause();
    record.placeholder.after(record.panel);
    this.layer = null;
  }

  openPhoto(index) {
    const button = this.panels.get('photos')?.panel.querySelectorAll('.anr-ap__galitem')[index - 1];
    if (!button) return;
    const thumb = button.querySelector('img');
    const image = this.lightbox.querySelector('img');
    for (const dimension of ['width', 'height']) {
      const value = Number(thumb?.getAttribute(dimension));
      if (value > 0) image.setAttribute(dimension, String(value));
      else image.removeAttribute(dimension);
    }
    image.src = button.dataset.anrFull;
    image.alt = thumb?.alt || '';
    this.photoOpener = button;
    if (!this.lightbox.open) this.lightbox.showModal();
  }

  restorePhotoFocus() {
    if (this.dialog.open) this.photoOpener?.focus({ preventScroll: true });
  }
}

if (!customElements.get('anr-artist-experience')) customElements.define('anr-artist-experience', ArtistExperience);
